/**
 * Locale Configuration - Dynamic Builder from Database
 *
 * Source of Truth: system_locale_configs table
 * - Enabled locales: is_enabled = true (currently 36 neutral primaries)
 * - Fallback chains: every enabled locale → en (single-hop; see
 *   buildFallbackConfig). Regional variants are intentionally disabled at
 *   the config level; i18next's nonExplicitSupportedLngs handles stale
 *   regional browser preferences by stripping the region.
 * - RTL detection from is_rtl column.
 *
 * ESLint rule `no-hardcoded-locale-arrays` enforces this module.
 */

import type { LocaleConfig } from '@/hooks/useLocaleConfigs';
import { 
  BOOTSTRAP_LOCALE, 
  DEFAULT_REGIONAL_LOCALE,
  isRtlLocaleBootstrap,
  getBaseLanguage,
} from '@/constants/localization';

// Re-export for convenience
export { BOOTSTRAP_LOCALE, DEFAULT_REGIONAL_LOCALE, getBaseLanguage } from '@/constants/localization';

/**
 * Build supported locales array from database configs
 * @param configs - Array of LocaleConfig from useLocaleConfigs()
 * @returns Array of enabled locale codes
 */
export function buildSupportedLocales(configs: LocaleConfig[]): string[] {
  return configs.filter(c => c.isEnabled).map(c => c.locale);
}

/**
 * Build fallback chain from database configs.
 *
 * Neutral-primary architecture (2026-05-28): system_locale_configs has only
 * 36 primary locales enabled; all 79 regional variants are is_enabled=false.
 * Every enabled config is therefore a primary, so the per-locale fallback
 * chain reduces to a single hop — <locale> → en — except for BOOTSTRAP_LOCALE
 * itself, which never needs a fallback entry.
 *
 * Runtime safety net for stale regional preferences (e.g. a returning user
 * whose browser still sends "pt-BR" from a previous session) is provided by
 * i18next's `nonExplicitSupportedLngs: true` in src/i18n/config.ts — when an
 * incoming locale isn't in supportedLngs, i18next strips the region and
 * looks up the base ('pt' here, which IS one of the 36 primaries). That
 * happens at the i18next layer, not here.
 *
 * The Step-1 regional→primary→en branch below is retained as defensive code
 * in case any config row ever ships with `primaryLocale` set — though under
 * the neutral-primary architecture this branch should never execute.
 *
 * CRITICAL: Ultimate fallback is BOOTSTRAP_LOCALE ('en'), not 'en-US'.
 * ESLint rule `no-hardcoded-locale-arrays` enforces this.
 *
 * @param configs - Array of LocaleConfig from useLocaleConfigs()
 * @returns i18next fallbackLng configuration object
 */
export function buildFallbackConfig(configs: LocaleConfig[]): Record<string, string[]> {
  const fallback: Record<string, string[]> = {};

  for (const config of configs) {
    if (config.primaryLocale) {
      // Defensive: handles a hypothetical regional row sneaking back in.
      // No such row exists today (2026-05-28); branch should be dead.
      fallback[config.locale] = [config.primaryLocale, BOOTSTRAP_LOCALE];
    } else if (config.locale !== BOOTSTRAP_LOCALE && config.locale !== DEFAULT_REGIONAL_LOCALE) {
      // Neutral primary → en. The 36-locale case for the entire app today.
      fallback[config.locale] = [BOOTSTRAP_LOCALE];
    }
  }

  // Default fallback for any unmatched locale — MUST be 'en' (source language)
  fallback['default'] = [BOOTSTRAP_LOCALE];

  return fallback;
}

/**
 * Build RTL locale set from database is_rtl column
 * Returns a Set for O(1) lookup performance
 * @param configs - Array of LocaleConfig from useLocaleConfigs()
 * @returns Set of RTL locale codes
 */
export function buildRtlLocales(configs: LocaleConfig[]): Set<string> {
  return new Set(configs.filter(c => c.isRtl).map(c => c.locale));
}

/**
 * Create RTL checker function from database configs
 * More accurate than bootstrap version - includes all regional variants
 * @param configs - Array of LocaleConfig from useLocaleConfigs()
 * @returns Function that checks if a locale is RTL
 */
export function createRtlChecker(configs: LocaleConfig[]): (locale: string) => boolean {
  const rtlSet = buildRtlLocales(configs);
  return (locale: string) => rtlSet.has(locale);
}

/**
 * Bootstrap configuration for i18next initialization
 * CRITICAL: Uses 'en' as ultimate fallback (source language)
 * Database configs will override this at runtime via useI18nInitializer
 * 
 * ESLint rule `no-hardcoded-locale-arrays` enforces:
 * - Minimal bootstrap array (not 50+ hardcoded locales)
 * - Ultimate fallback must be 'en', not 'en-US'
 */
export const BOOTSTRAP_I18N_CONFIG = {
  supportedLngs: [BOOTSTRAP_LOCALE], // Minimal: ['en'] only
  fallbackLng: { default: [BOOTSTRAP_LOCALE] }, // 'en' is ultimate fallback
};

/**
 * Bootstrap RTL check (uses base language extraction)
 * Use createRtlChecker() with database configs for runtime accuracy
 */
export const isRtlLocale = isRtlLocaleBootstrap;
